import React, { useState } from 'react';
import { Stack, Typography, Paper, Button } from '@mui/material';
import type { HoldList, ShowList } from 'api-client/models';
import { MobileHoldCard } from './ShowDetails/holds/MobileHoldCard';

interface CalendarAgendaProps {
  days: Date[];
  getHolds: (date: Date) => HoldList[];
  getShows: (date: Date) => ShowList[];
  canCreate: (date: Date) => boolean;
  onCreate: (date: Date) => void;
  onShow: (showId: string) => void;
}

/** Phone agenda includes empty days so single-day creation remains available without dragging. */
export const CalendarAgenda: React.FC<CalendarAgendaProps> = ({
  days,
  getHolds,
  getShows,
  canCreate,
  onCreate,
  onShow,
}) => {
  const [showEarlierDates, setShowEarlierDates] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth = days.some(
    (date) => date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth(),
  );
  const hasEarlierDates = isCurrentMonth && days.some((date) => date < today);
  const visibleDays = hasEarlierDates && !showEarlierDates ? days.filter((date) => date >= today) : days;
  return (
    <Stack spacing={2} aria-label="Monthly agenda">
      {hasEarlierDates && (
        <Button onClick={() => setShowEarlierDates(!showEarlierDates)} aria-expanded={showEarlierDates}>
          {showEarlierDates ? 'Hide earlier dates' : 'Show earlier dates'}
        </Button>
      )}
      {visibleDays.map((date) => {
        const holds = getHolds(date);
        // A confirmed hold already represents its parent show on this date.
        const shows = getShows(date).filter(
          (show) => !holds.some((hold) => hold.show === show.id && hold.status === 'confirmed'),
        );
        return (
          <Stack key={date.toISOString()} spacing={1}>
            <Typography variant="subtitle2">
              {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </Typography>
            {holds.map((hold) => (
              <Stack key={hold.id} spacing={0.5}>
                <Typography variant="body2">{hold.showTitle || 'Untitled Hold'}</Typography>
                <MobileHoldCard hold={hold} />
              </Stack>
            ))}
            {shows.map((show) => (
              <Paper key={show.id} variant="outlined" sx={{ p: 1 }}>
                <Button onClick={() => onShow(show.id)}>{show.title} · Confirmed</Button>
              </Paper>
            ))}
            {holds.length === 0 && shows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No bookings
              </Typography>
            )}
            {canCreate(date) && (
              <Button variant="outlined" onClick={() => onCreate(date)}>
                Add hold for {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </Button>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
